import { Color, Spacing } from '@persona-id/design-tokens';
import { isEmpty, keyBy, merge } from 'lodash/fp';
import { MutableRefObject, RefObject, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import useIsITEInternal from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsITEInternal';
import { WorkflowVersionMap } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { WorkflowDiffStepPanel } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVersionDiffWorkflow/WorkflowStepDiffPanel/WorkflowStepDiffPanel';
import {
  VersionDiff,
  VersionDiffChangeType,
  VersionDiffLinkedWorkflowStep,
  WorkflowVersionDiff,
  WorkflowVersionStepArrayDiff,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/types';
import { valueDiffIsNotEmpty } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/utils';
import { getActionNodeSteps } from 'dashboard/apps/InquiryTemplateEditor/TemplateWorkflowEditor/utils';
import ToggleButton from 'dashboard/apps/InquiryTemplateEditor/ToggleButton';
import WorkflowTree from 'dashboard/apps/InquiryTemplateEditor/WorkflowTree';
import { FlowEditorHighlightColorVariant } from 'dashboard/components/FlowEditorNodeHighlighter';
import PanZoomBox from 'dashboard/components-lib/PanZoomBox';
import { createMockDirectTrigger } from 'dashboard/lib/stubs/trigger';
import { createInputStep, flattenSteps } from 'dashboard/lib/workflows';
import { WorkflowSchema } from 'dashboard/models/workflow-schema';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import useToggle from 'lib/hooks/useToggle';
import { assertNever } from 'lib/utils/types';
import { buildGraph } from 'lib/workflows';

const ToggleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const ToggleLabel = styled(Paragraph).attrs(() => ({ size: 'md' }))`
  color: ${({ theme }) => theme.color.text.tertiary};
  margin-right: ${Spacing[2]};
`;

const WorkflowDiffContainer = styled.div`
  height: 700px;
  display: flex;
  border-radius: ${({ theme }) => theme.card.borderRadius};
  border: 1px solid #e6e6ea;
  overflow: hidden;
`;

export const extractWorkflowActionSequenceChanges = ({
  workflowVersion,
  triggerStepToken,
  workflowVersionChanges,
}: {
  workflowVersion: WorkflowVersion;
  triggerStepToken: string;
  workflowVersionChanges: WorkflowVersionDiff | null;
}): WorkflowVersionStepArrayDiff => {
  if (workflowVersionChanges == null) return {};
  const actionNodeSteps = flattenSteps(
    getActionNodeSteps(workflowVersion.attributes['steps-config'] || [], triggerStepToken),
  );

  const actionNodeStepTokens = actionNodeSteps.map((actionNode) => actionNode.token);

  const stepsConfigDiff = workflowVersionChanges.changes['steps-config'] || {};
  return Object.fromEntries(
    Object.entries(stepsConfigDiff).filter(([workflowStepToken, workflowStepDiff]) => {
      if (actionNodeStepTokens.includes(workflowStepToken)) {
        switch (workflowStepDiff['change-type']) {
          case VersionDiffChangeType.MODIFIED:
            // We consider stepChanges to be empty under two conditions:
            // 1. stepChanges and branchChanges is null
            // 2. stepChanges only contains path related changes
            return (
              (workflowStepDiff?.changes?.step != null &&
                Object.keys(workflowStepDiff.changes.step).filter((key) => key !== 'path').length >
                  0) ||
              (workflowStepDiff?.changes?.branches != null &&
                valueDiffIsNotEmpty(workflowStepDiff.changes.branches))
            );

          case VersionDiffChangeType.ADDED:
          case VersionDiffChangeType.REMOVED:
            return true;
          default:
            assertNever(workflowStepDiff['change-type']);
        }
      }
    }),
  );
};

const WorkflowTemplateVersionDiffTable = ({
  workflowSchema,
  targetWorkflowVersion,
  baseWorkflowVersion,
  workflowVersionChanges,
  targetActionSequence,
  baseActionSequence,
}: {
  workflowSchema: WorkflowSchema;
  targetWorkflowVersion: WorkflowVersion;
  baseWorkflowVersion: WorkflowVersion | null;
  workflowVersionChanges: WorkflowVersionDiff | null;
  targetActionSequence: VersionDiffLinkedWorkflowStep;
  baseActionSequence: VersionDiffLinkedWorkflowStep | null;
}) => {
  // We need to calculate changes relevant to both versions upfront to determine whether
  // The differ card should be expanded or not
  const workflowActionSequenceChanges = useMemo(() => {
    const targetWorkflowActionSequenceChanges = extractWorkflowActionSequenceChanges({
      workflowVersion: targetWorkflowVersion,
      triggerStepToken: targetActionSequence.workflowStep.token,
      workflowVersionChanges,
    });
    if (baseWorkflowVersion && baseActionSequence) {
      const baseWorkflowActionSequenceChanges = extractWorkflowActionSequenceChanges({
        workflowVersion: baseWorkflowVersion,
        triggerStepToken: baseActionSequence.workflowStep.token,
        workflowVersionChanges,
      });

      return merge(targetWorkflowActionSequenceChanges, baseWorkflowActionSequenceChanges);
    }
    return targetWorkflowActionSequenceChanges;
  }, [
    baseWorkflowVersion,
    targetWorkflowVersion,
    baseActionSequence,
    targetActionSequence,
    workflowVersionChanges,
  ]);
  const noChanges = workflowVersionChanges == null || isEmpty(workflowActionSequenceChanges);

  const [showUnchanged, { toggle: toggleShowUnchanged }] = useToggle(false);

  const isITEInternal = useIsITEInternal();
  const [showCurrentVersionTree, setShowCurrentVersionTree] = useState(true);
  const [workflowVersion, actionSequence] = useMemo(
    () =>
      showCurrentVersionTree || baseWorkflowVersion == null || baseActionSequence == null
        ? [targetWorkflowVersion, targetActionSequence]
        : [baseWorkflowVersion, baseActionSequence],
    [
      showCurrentVersionTree,
      targetWorkflowVersion,
      baseWorkflowVersion,
      targetActionSequence,
      baseActionSequence,
    ],
  );

  useEffect(() => {
    setActiveNodeToken(actionSequence.workflowStep.token);
  }, [actionSequence]);

  const [activeNodeToken, setActiveNodeToken] = useState<string>(actionSequence.workflowStep.token);
  const [focusedNodeToken, setFocusedNodeToken] = useState<string | null>(activeNodeToken);
  const [showFullWorkflowTree, { toggle: toggleFullWorkflowTree }] = useToggle(false);
  const stepsConfig = useMemo(() => {
    const stepsConfig = workflowVersion.attributes['steps-config'] || [];
    return showFullWorkflowTree
      ? stepsConfig
      : getActionNodeSteps(stepsConfig || [], actionSequence.workflowStep.token);
  }, [workflowVersion, showFullWorkflowTree, actionSequence]);

  const tokensToSteps = useMemo(() => buildGraph(stepsConfig), [stepsConfig]);
  const selectNode = (selectedNodeToken: string) => {
    setFocusedNodeToken(selectedNodeToken);
    setActiveNodeToken(selectedNodeToken);
  };

  // Generate map of workflow token -> Color for workflow tree to render
  const workflowStepHighlightMap = Object.entries(workflowActionSequenceChanges).reduce<
    Record<string, Color>
  >((acc, [key, value]) => {
    acc[key] = FlowEditorHighlightColorVariant[value['change-type']];
    return acc;
  }, {});

  const trigger = createMockDirectTrigger(workflowVersion?.relationships?.trigger?.data?.id);
  const inputStep = useMemo(() => createInputStep({ trigger }), [trigger]);

  return (
    <Card>
      <Card.Body>
        <Card.Title
          actions={[
            <ToggleSlider
              key='show-unchanged-toggle'
              titleOn='displaying all values'
              titleOff='only showing changes'
              labelOn='Show unchanged'
              labelOff='Show unchanged'
              value={showUnchanged}
              onChange={toggleShowUnchanged}
            />,
          ]}
        >
          {actionSequence.workflowStep.name}
        </Card.Title>

        {noChanges && !showUnchanged ? (
          <Label color='secondary' size='lg'>
            No changes made
          </Label>
        ) : (
          <>
            {baseWorkflowVersion != null && (
              <ToggleButton
                activeButtonLabel='Current Draft Version'
                inactiveButtonLabel='Last Published Version'
                isActive={showCurrentVersionTree}
                setIsActive={setShowCurrentVersionTree}
              />
            )}
            <WorkflowDiffContainer>
              <PanZoomBox
                utilityButton={
                  isITEInternal ? (
                    <ToggleWrapper>
                      <ToggleLabel>Full tree</ToggleLabel>
                      <ToggleSlider
                        value={showFullWorkflowTree}
                        onChange={toggleFullWorkflowTree}
                      />
                    </ToggleWrapper>
                  ) : null
                }
                preventPanOnScroll
                showZoomControls
                withExternalZoomControls
                useKeydownShortcuts
              >
                <WorkflowTree
                  inputStep={inputStep}
                  steps={stepsConfig}
                  activeNodeToken={activeNodeToken}
                  nodeGraph={tokensToSteps}
                  validationErrors={null}
                  editable={false}
                  workflowStepHighlightMap={workflowStepHighlightMap}
                  selectNode={selectNode}
                  focusedNodeToken={focusedNodeToken}
                  setConditionalRouteToEdit={() => undefined}
                  handleAddFirstChildNode={null}
                  handleAddNode={null}
                  moveStep={null}
                />
              </PanZoomBox>
              <WorkflowDiffStepPanel
                workflowVersionId={workflowVersion.id}
                workflowActionSequenceChanges={workflowActionSequenceChanges}
                flattenedStepsConfig={flattenSteps(stepsConfig)}
                showCurrentVersionTree={showCurrentVersionTree}
                focusedNodeToken={focusedNodeToken}
                workflowSchema={workflowSchema}
                setActiveNodeToken={setActiveNodeToken}
                setFocusedNodeToken={setFocusedNodeToken}
                showUnchanged={showUnchanged}
                trigger={trigger}
              />
            </WorkflowDiffContainer>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

const keyWorkflowVersionMapByWorkflowId = (workflowVersionMap: WorkflowVersionMap) =>
  keyBy((workflowVersion) => workflowVersion.relationships.workflow.data.id, workflowVersionMap);

const keyActionSequenceByWorkflowStepToken = (actionSequences: VersionDiffLinkedWorkflowStep[]) =>
  keyBy((actionSequence) => actionSequence.workflowStep.token, actionSequences);

interface InquiryTemplateVersionDiffWorkflowProps {
  versionDiff: VersionDiff;
  workflowSchema: WorkflowSchema;
  targetWorkflowVersionMap: WorkflowVersionMap;
  baseWorkflowVersionMap: WorkflowVersionMap;
  actionSequenceTokenToRefMap: MutableRefObject<Record<string, RefObject<HTMLDivElement>>>;
  targetActionSequences: VersionDiffLinkedWorkflowStep[];
  baseActionSequences: VersionDiffLinkedWorkflowStep[];
}

const InquiryTemplateVersionDiffWorkflow = ({
  versionDiff,
  workflowSchema,
  targetWorkflowVersionMap,
  baseWorkflowVersionMap,
  actionSequenceTokenToRefMap,
  targetActionSequences,
  baseActionSequences,
}: InquiryTemplateVersionDiffWorkflowProps) => {
  const { 'workflow-versions': workflowVersionDiff } = versionDiff.inquiryTemplateVersionDiff;
  const baseWorkflowIdVersionMap = useMemo(
    () => keyWorkflowVersionMapByWorkflowId(baseWorkflowVersionMap),
    [baseWorkflowVersionMap],
  );
  // Workflow Step Tokens are consistent between different workflow versions
  // Using this key we can find the corresponding matching action sequence in the published workflow version
  const baseActionSequencesByWorkflowStepToken = useMemo(
    () => keyActionSequenceByWorkflowStepToken(baseActionSequences),
    [baseActionSequences],
  );

  return (
    <>
      {targetActionSequences.map((targetActionSequence, index) => {
        const targetWorkflowVersion =
          targetWorkflowVersionMap[targetActionSequence.workflowVersionId];
        const workflowId = targetWorkflowVersion.relationships.workflow.data.id;

        const baseWorkflowVersion = baseWorkflowIdVersionMap[workflowId];
        const baseActionSequence =
          baseActionSequencesByWorkflowStepToken[targetActionSequence.workflowStep.token];

        // We use workflow tokens as the identifier in the workflow version
        // diff object since workflow version tokens are different between inquiry template versions
        const workflowVersionChanges = workflowVersionDiff[workflowId];

        return (
          <>
            <div
              key={index}
              ref={
                (actionSequenceTokenToRefMap.current[targetActionSequence.token] ??= {
                  current: null,
                })
              }
            >
              <WorkflowTemplateVersionDiffTable
                workflowSchema={workflowSchema}
                targetWorkflowVersion={targetWorkflowVersion}
                baseWorkflowVersion={baseWorkflowVersion}
                workflowVersionChanges={workflowVersionChanges}
                targetActionSequence={targetActionSequence}
                baseActionSequence={baseActionSequence}
              />
            </div>
            <Spacer height={5} />
          </>
        );
      })}
    </>
  );
};

export default InquiryTemplateVersionDiffWorkflow;
